import { useDispatch } from "react-redux";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import eventHandler from "../../SharedModule/utils/eventHandler";
import { ClientsService } from "../services/clients";

export type ToggleClientActivationPayload = {
  ClientId: number;
};

// Shared by the deactivate and reactivate flows: both show the same confirm
// modal shape, call the matching external/internal service method, and
// handle success/error toasts identically - only the verb and which pair of
// service methods to call differ.
export const useToggleClientActivation = (
  action: "deactivate" | "reactivate",
  showModal: (options: any) => Promise<void>,
  closeModal: () => void,
  isExternalClient: boolean,
  setIsLoadingDeactivate: Function
) => {
  const dispatch = useDispatch();
  const label = action === "deactivate" ? "Deactivate" : "Reactivate";

  const toggleClientActivation = async (
    payload: ToggleClientActivationPayload
  ) => {
    const options = {
      title: `${label} ${isExternalClient ? "External" : "Internal"} Client?`,
      text: `Are you sure you want to ${action} this ${
        isExternalClient ? "External" : "Internal"
      } client?`,
      btnLabel: `Yes, ${action} client`,
    };
    await showModal(options);
    setIsLoadingDeactivate(true);
    closeModal();

    const serviceCall =
      action === "deactivate"
        ? isExternalClient
          ? ClientsService.deactivateExternalClient(payload.ClientId)
          : ClientsService.deactivateInternalClient(payload.ClientId)
        : isExternalClient
          ? ClientsService.reactivateExternalClient(payload.ClientId)
          : ClientsService.reactivateInternalClient(payload.ClientId);

    serviceCall
      .then((data) => {
        if (!data.success) {
          dispatch(
            addToast({
              mode: "error",
              message: data.errors[data.errors.length - 1],
            })
          );
          setIsLoadingDeactivate(false);
          return false;
        } else {
          dispatch(
            addToast({
              mode: "success",
              message: data.message,
            })
          );
          setIsLoadingDeactivate(false);
          eventHandler.dispatch("toast action", { isClosed: false });
          return true;
        }
      })
      .catch((err) => {
        dispatch(
          addToast({
            mode: "error",
            message: err?.errors?.[err.errors.length - 1] || "Unexpected error",
          })
        );
        setIsLoadingDeactivate(false);
        return false;
      });

    return serviceCall;
  };

  return toggleClientActivation;
};
